import { caseDbApiEndpoint } from './apiOrigin';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from './csrf';

export const DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT = 'casedb:document-operation-undo-receipt';
export const DOCUMENT_OPERATION_UNDO_STACK_LIMIT = 20;
export const DOCUMENT_OPERATION_UNDO_DEFAULT_TTL_MS = 2 * 60 * 60 * 1000;

const COPY_MODIFIER_KEYS = ['ctrlKey', 'metaKey', 'altKey'];

export const resolveDocumentDragOperation = (event, payload = {}) => {
  const hasLiveModifierState = Boolean(event)
    && COPY_MODIFIER_KEYS.some(key => Object.prototype.hasOwnProperty.call(event, key) || key in event);

  if (hasLiveModifierState) {
    return COPY_MODIFIER_KEYS.some(key => event?.[key]) ? 'copy' : 'move';
  }

  return payload?.operation === 'copy' ? 'copy' : 'move';
};

export const normalizeDocumentMutationSourceKey = ({ key, caseId, currentFolder = '' }) => {
  const normalizedKey = String(key || '').replace(/^\/+/, '');
  if (!normalizedKey) return normalizedKey;

  // Search, persisted selections, and Pre-Case Lead Documents already carry
  // canonical roots. Their folder context must never be prepended again.
  if (/^cases\/[^/]+\/\{\{Name\}\}\//.test(normalizedKey)
      || /^leads\/[^/]+\//.test(normalizedKey)) {
    return normalizedKey;
  }
  if (normalizedKey.startsWith('{{Name}}/')) {
    return `cases/${caseId}/${normalizedKey}`;
  }
  if (normalizedKey.startsWith('cases/')) {
    return normalizedKey;
  }

  const relativeFolder = String(currentFolder || '')
    .replace(/^\{\{Name\}\}\/?/, '')
    .replace(/^\/+|\/+$/g, '');
  const relativePrefix = relativeFolder && relativeFolder !== 'root'
    ? `${relativeFolder}/`
    : '';
  return `cases/${caseId}/{{Name}}/${relativePrefix}${normalizedKey}`;
};

export const settleDocumentMutationRefreshes = async (refreshers = []) => {
  const callbacks = (Array.isArray(refreshers) ? refreshers : [])
    .filter(refresh => typeof refresh === 'function');
  const results = await Promise.allSettled(
    callbacks.map(refresh => Promise.resolve().then(refresh))
  );
  const errors = results
    .filter(result => result.status === 'rejected')
    .map(result => result.reason);

  return {
    success: errors.length === 0,
    errors,
    warning: errors.length > 0
      ? 'The document change completed, but the listing could not refresh. Reload the page to see the latest state.'
      : '',
  };
};

const displayNameForKey = (key) => {
  const normalized = String(key || '').replace(/\/+$/, '');
  return normalized.split('/').pop() || normalized || 'Document item';
};

export const getDocumentUndoUnavailableMessage = (result) => {
  const undo = result?.undo;
  if (!undo || undo.available !== false) return '';
  if (undo.reason === 'too_large') {
    const max = Number(undo.max_objects || 500);
    return `The operation completed, but undo is limited to ${max.toLocaleString()} objects for safety.`;
  }
  return String(undo.message || 'The operation completed, but it could not be made safely undoable.');
};

export const publishDocumentUndoReceipt = ({
  caseId,
  userId = null,
  operation,
  result,
  sourceKeys = [],
  label = '',
}) => {
  const undo = result?.undo;
  const receiptId = String(
    undo?.receipt_id || result?.receipt_id || result?.operation?.receipt_id || ''
  ).trim();
  const hasDurableReceipt = /^[a-f0-9]{32}$/.test(receiptId);
  // Recent actions is durable and opaque by design. Never fall back to a
  // global browser event containing signed tokens or source paths; a backend
  // that cannot return a registered receipt cannot advertise browser-owned
  // undo for the operation.
  if (!hasDurableReceipt) {
    return false;
  }
  if (!['move', 'copy', 'delete'].includes(operation)) return false;

  window.dispatchEvent(new CustomEvent(DOCUMENT_OPERATION_UNDO_RECEIPT_EVENT, {
    detail: {
      caseId: String(caseId ?? ''),
      ...(userId === null || userId === undefined ? {} : { userId: String(userId) }),
      operation,
      receiptId,
      objectCount: Number(undo?.object_count || result?.object_count || sourceKeys.length || 1),
      label: label || `${operation === 'copy' ? 'Copy' : operation === 'move' ? 'Move' : 'Delete'} ${sourceKeys.length || 1} item${sourceKeys.length === 1 ? '' : 's'}`,
      createdAt: Date.now(),
      expiresAt: Date.now() + (Math.max(1, Number(undo?.expires_in || 7200)) * 1000),
    },
  }));
  return true;
};

export const appendDocumentUndoReceipt = (stack, receipt, limit = DOCUMENT_OPERATION_UNDO_STACK_LIMIT) => {
  if (!receipt?.undoToken || !receipt?.caseId) return Array.isArray(stack) ? stack : [];
  return [...(Array.isArray(stack) ? stack : []), receipt].slice(-Math.max(1, limit));
};

export const pruneExpiredDocumentUndoReceipts = (stack, now = Date.now()) => (
  (Array.isArray(stack) ? stack : []).filter(receipt => {
    const expiresAt = Number(receipt?.expiresAt || 0)
      || (Number(receipt?.createdAt || 0) + DOCUMENT_OPERATION_UNDO_DEFAULT_TTL_MS);
    return Boolean(receipt?.undoToken && receipt?.caseId && expiresAt > now);
  })
);

export const isDocumentUndoKeyboardEvent = (event, hasReceipt = false) => {
  if (!hasReceipt || !event || event.defaultPrevented || event.isComposing) return false;
  if (String(event.key || '').toLowerCase() !== 'z') return false;
  if (!(event.ctrlKey || event.metaKey) || event.shiftKey || event.altKey) return false;

  const target = event.target;
  const tagName = String(target?.tagName || '').toLowerCase();
  if (['input', 'textarea', 'select'].includes(tagName) || target?.isContentEditable) return false;
  if (typeof target?.closest === 'function' && target.closest('[contenteditable="true"]')) return false;

  return true;
};

export const executeDocumentUndoReceipt = async ({ receipt, caseId, dispatch, fetchImpl = fetch }) => {
  if (!receipt?.undoToken || String(receipt.caseId) !== String(caseId)) {
    throw new Error('That undo action does not belong to the current case.');
  }

  const operationId = `document-undo-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const sourceKeys = Array.isArray(receipt.sourceKeys) ? receipt.sourceKeys : [];
  const files = sourceKeys.length > 0
    ? sourceKeys.slice(0, 100).map(key => ({ name: displayNameForKey(key) }))
    : [{ name: receipt.label || 'Document operation' }];

  dispatch?.({
    type: 'START_BATCH',
    payload: {
      id: operationId,
      operation: 'undo',
      operationLabel: receipt.operation === 'copy' ? 'Undoing copy' : 'Undoing move',
      totalFiles: Math.max(1, Number(receipt.objectCount || files.length)),
      files,
      statusMessage: 'Restoring the previous document state',
      createdAt: Date.now(),
    },
  });

  try {
    const csrfToken = await ensureCaseDbCsrfToken({
      expectedUserId: receipt.userId ?? null,
    });
    if (!csrfToken) {
      throw new Error('Your session could not be verified. Sign in again and retry.');
    }
    const response = await fetchImpl(caseDbApiEndpoint('s3_upload.php'), {
      method: 'PUT',
      headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'PUT'),
      credentials: 'include',
      body: JSON.stringify({
        action: 'undo_document_operation',
        case_id: caseId,
        undo_token: receipt.undoToken,
      }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result?.success) {
      throw new Error(result?.message || 'CaseDB could not safely undo that operation.');
    }

    dispatch?.({ type: 'COMPLETE_UPLOAD', payload: { id: operationId } });
    return result;
  } catch (error) {
    dispatch?.({
      type: 'FAIL_UPLOAD',
      payload: { id: operationId, error: error.message || 'Undo failed' },
    });
    throw error;
  }
};
